import { Mail01, PhoneCall01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import WalLogoIcon from 'src/assets/img/logo/logo-icon.svg';
import CardCmp from 'src/components/CardCmp';
import PhoneNumberDisplay from 'src/components/PhoneNumberDisplay';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import useOrgBranding from 'src/hooks/useOrgBranding';
import { currentUser } from 'src/redux/auth.slice';
import { useAppSelector } from 'src/redux/hooks';
import { copyToClipboard, extractMcNumber, getShortName, onError } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

import { useRadarData } from '../../hooks/useRadarData';

import { SHARE_MOBILE_CARDCMP } from './common/shareMobile.styles';

// isShareMobile: share-link mobile sheet (Order tab) — Figma card styling, same data + interactions.
const CarrierDetailsSection = ({ isShareMobile = false }: { isShareMobile?: boolean }) => {
    const { t } = useTranslation();
    const { selectedLeg, trackOrderDetail, isDetailLoading } = useRadarData();
    const user = useAppSelector(currentUser);
    const orgBranding = useOrgBranding();
    const carrierNameRef = useRef<HTMLParagraphElement>(null);
    const trackingLinkRef = useRef<HTMLAnchorElement>(null);
    // Figma share-mobile avatar: 20px, 6px radius.
    const avatarSizeClass = isShareMobile ? '!w-5 !h-5 !min-w-5' : '';

    const carrier = useMemo(() => selectedLeg?.carrier ?? null, [selectedLeg]);
    // Auth state first; fall back to the order payload so the public share link — which has no
    // logged-in user — still renders the sales rep's contact icons.
    const salesRep = useMemo(() => user?.salesRep ?? trackOrderDetail?.salesRep, [user?.salesRep, trackOrderDetail?.salesRep]);
    // No carrier, masked carrier, or the legacy hardcoded name — present the org identity.
    // carrier.isOrgCarrier is the BE's explicit marker for a masked carrier; the name comparisons
    // stay as a fallback. Comparing against orgBranding.name alone is not enough on the public
    // share page, which has no appearance data and so falls back to a different org name.
    const isOrgCarrier = !carrier?.name || carrier.isOrgCarrier === true || carrier.name === orgBranding.name || carrier.name === 'Western Alliance';
    // Org identity → MC from Organization Settings; a real carrier → its own mcId. Both reduced to
    // digits under a "MC #" label (extractMcNumber), same as OrderStatus/CarrierAssignment/WALHQ.
    const mcNumber = useMemo(() => {
        if (isOrgCarrier) return extractMcNumber(orgBranding.mcNumber);

        return extractMcNumber(carrier?.mcId);
    }, [isOrgCarrier, orgBranding.mcNumber, carrier?.mcId]);

    // Tapping a contact icon copies the value — same helper + toast as CarrierAssignment's
    // copy buttons, so the behaviour matches the rest of the portal.
    const handleCopyPhone = useCallback(() => {
        copyToClipboard(salesRep?.phone ?? '');
        WalToast.copy(t('Common.Copied_to_clipboard'), '', 'copy-msg');
    }, [salesRep?.phone, t]);

    const handleCopyEmail = useCallback(() => {
        copyToClipboard(salesRep?.salesrepemail ?? '');
        WalToast.copy(t('Common.Copied_to_clipboard'), '', 'copy-msg');
    }, [salesRep?.salesrepemail, t]);

    return (
        <CardCmp
            headerTitle="Carrier Details"
            isAccordion={false}
            isFooter={false}
            headerParentClassName={`!border-b-0 px-4 radar-card-title ${isShareMobile ? SHARE_MOBILE_CARDCMP.headerParentClassName : ''}`}
            childrenClassName={`px-4 pb-3 ${isShareMobile ? SHARE_MOBILE_CARDCMP.childrenClassName : ''}`}
            cardClassName={isShareMobile ? SHARE_MOBILE_CARDCMP.cardClassName : ''}
        >
            <div className="flex flex-col gap-y-2.5">
                {/* Carrier name + logo row */}
                <div className="flex items-center gap-x-1.5">
                    {isDetailLoading ? (
                        <div className="table-profile-customer-circle-sm flex-none text-xs custom-loading">AB</div>
                    ) : carrier?.image ? (
                        <img src={carrier.image} className={`table-profile-icn-circle-sm flex-none !rounded-md ${avatarSizeClass}`} onError={onError} />
                    ) : isOrgCarrier ? (
                        // Org identity (appearance table) — uploaded brand logo, black WAL icon as fallback
                        orgBranding.logoSrc ? (
                            <img src={orgBranding.logoSrc} alt={orgBranding.name} className={`table-profile-icn-circle-sm flex-none !rounded-md object-contain ${avatarSizeClass}`} onError={onError} />
                        ) : (
                            <div className={`table-profile-customer-circle-sm flex-none !rounded-md !bg-utilityBlack flex items-center justify-center p-1 ${avatarSizeClass}`}>
                                <img src={WalLogoIcon} alt={orgBranding.name} className="w-full h-full object-contain" />
                            </div>
                        )
                    ) : (
                        <div className={`table-profile-customer-circle-sm flex-none text-xs ${avatarSizeClass}`}>{getShortName(carrier.name)}</div>
                    )}

                    {/* Name and MC # sit on one line, split by a hairline divider */}
                    <div className="flex-1 min-w-0 flex items-center gap-x-1.5">
                        <TruncateTextCmp message={isDetailLoading ? '' : carrier?.name ?? orgBranding.name} refName={carrierNameRef}>
                            <p ref={carrierNameRef} className={`text-[12px] font-medium leading-[18px] text-grayLight900 truncate ${isDetailLoading ? 'custom-loading' : ''}`}>
                                {isDetailLoading ? 'lorem ipsum dolor' : carrier?.name ?? orgBranding.name}
                            </p>
                        </TruncateTextCmp>
                        {isDetailLoading ? (
                            <>
                                <div className={`w-px h-3 bg-borderPrimary flex-none ${isShareMobile ? '!h-4 !bg-utilityGray200' : ''}`} />
                                <p className="text-gray500 text-xs leading-[18px] font-normal custom-loading w-20 flex-none">MC #000000</p>
                            </>
                        ) : (
                            mcNumber && (
                                <>
                                    <div className={`w-px h-3 bg-borderPrimary flex-none ${isShareMobile ? '!h-4 !bg-utilityGray200' : ''}`} />
                                    <p className={`text-gray500 text-xs leading-[18px] font-normal flex-none ${isShareMobile ? '!text-grayLight600' : ''}`}>MC #{mcNumber}</p>
                                </>
                            )
                        )}
                    </div>

                    <div className="flex items-center gap-x-2 my-auto flex-none">
                        {salesRep?.phone && (
                            <TooltipCmp message={<PhoneNumberDisplay phoneNumber={salesRep.phone} showLink={false} />} childrenClassName="flex">
                                <span className="text-textSecondary cursor-pointer transition-colors hover:text-primary" onClick={handleCopyPhone}>
                                    <PhoneCall01 className="w-4 h-4" />
                                </span>
                            </TooltipCmp>
                        )}
                        {salesRep?.salesrepemail && (
                            <TooltipCmp message={salesRep.salesrepemail} childrenClassName="flex">
                                <span className="text-textSecondary cursor-pointer transition-colors hover:text-primary" onClick={handleCopyEmail}>
                                    <Mail01 className="w-4 h-4" />
                                </span>
                            </TooltipCmp>
                        )}
                    </div>
                </div>

                {/* Tracking Link — the carrier's live GPS session, mirroring WAL Radar's Carrier
                    Details row. Present ONLY when WAL ticked "Show this tracking link to the
                    customer": the portal backend gates the column in SQL and sends null otherwise,
                    so an unshared link never reaches this component to be hidden client-side. */}
                {!isDetailLoading && selectedLeg?.trackingLink && (
                    <div className="flex items-center justify-between gap-x-4">
                        <p className="text-xs leading-[18px] text-textSecondary font-normal flex-none">{t('Radar.Tracking_Link')}</p>
                        <TruncateTextCmp message={selectedLeg.trackingLink} refName={trackingLinkRef}>
                            <a
                                ref={trackingLinkRef}
                                href={selectedLeg.trackingLink}
                                target="_blank"
                                // noreferrer alongside noopener: the carrier's tracking host must not
                                // receive the portal URL (and the customer's order id) as a referrer.
                                rel="noopener noreferrer"
                                className="block min-w-0 truncate text-right text-xs leading-[18px] font-medium text-primary hover:underline"
                            >
                                {selectedLeg.trackingLink}
                            </a>
                        </TruncateTextCmp>
                    </div>
                )}
            </div>
        </CardCmp>
    );
};

export default CarrierDetailsSection;
